import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { Button } from '@trqk/components';
import { constants, formatMailtoContent, locale } from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';

import {
    CurrentPlanAmounts,
    // CurrentPlanGapAnalysis,
    CurrentPlanWrapper,
    PortalSessionButton,
} from '.';

const { GET_SESSION_CURRENT_USER } = queryStrings;

const planLocale =
    locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.PUBLISHER_EDITION_PLAN;

interface CurrentPaidPlanProps {
    chargebeeInstance: null | ChargebeeInstance;
    isPublisherEdition: boolean;
    onButtonViewed(): void;
    /**
     * Callback invoked when the Chargebee subscription is changed
     */
    onSubscriptionChanged(): void;
    planSettings: Maybe<ChargebeePlanSettings>;
    subscriptionSettings: Maybe<PaymentPlanSubscriptionSettings>;
    width: number;
    usage: PaymentPlanUsage;
}

/**
 * Renders the current paid plan amounts, which are non-adjustable.
 * @param props - see {@link CurrentPaidPlanProps}
 */
export const CurrentPaidPlan = ({
    chargebeeInstance,
    isPublisherEdition,
    onButtonViewed,
    onSubscriptionChanged,
    planSettings,
    subscriptionSettings,
    width,
    usage,
}: CurrentPaidPlanProps): JSX.Element => {
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id;

    const bodyLines = [
        planLocale.SUPPORT_EMAIL_CONTENT.GREETING,
        `${planLocale.SUPPORT_EMAIL_CONTENT.BODY_PART_1} ${userId}`,
        planLocale.SUPPORT_EMAIL_CONTENT.BODY_PART_2,
        planLocale.SUPPORT_EMAIL_CONTENT.SIGN_OFF,
    ];

    const mailtoContentForPublisherSupport = useMemo(
        () =>
            formatMailtoContent({
                email: constants.SUBSCRIPTIONS_PUBLISHERS_EMAIL_ADDRESS,
                subject: planLocale.SUPPORT_EMAIL_CONTENT.SUBJECT,
                bodyLines,
            }),
        [userId],
    );

    // const subscription_gap_analysis_bmat_sourced =
    //     subscriptionSettings?.subscription_gap_analysis_bmat_sourced ?? false;
    // const subscription_gap_analysis_trqk_sourced =
    //     subscriptionSettings?.subscription_gap_analysis_trqk_sourced ?? false;

    return (
        <CurrentPlanWrapper
            planSettings={planSettings}
            subscriptionSettings={subscriptionSettings}
        >
            <div className="space-y-3" style={{ width }}>
                <CurrentPlanAmounts
                    label={
                        locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.SETTINGS
                            .QUARTERLY_ROYALTIES
                    }
                    type="QUARTERLY_ROYALTIES"
                    usageDetail={usage.royalties}
                    unlimited={isPublisherEdition === true}
                />
                {!isPublisherEdition && (
                    <CurrentPlanAmounts
                        label={
                            locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.SETTINGS
                                .DETECTION_TRACKS
                        }
                        type="DETECTIONS"
                        usageDetail={usage.detectionTracks}
                    />
                )}
                {/*
                    TODO: https://app.asana.com/0/1150917131612043/1201375140931903
                    When we implement Gap Analysis for trqk-sourced detections, we will need to
                    show a checkbox indicating whether the current plan includes gap analysis or not.
                */}
                {/* {subscriptionSettings?.subscription_plan_type !==
                    'CHARGEBEE_PLAN_SCALE' && (
                    <CurrentPlanGapAnalysis
                        checked={true}
                        canUseBmatDetections={
                            subscription_gap_analysis_bmat_sourced
                        }
                        canUseTrqkDetections={
                            subscription_gap_analysis_trqk_sourced
                        }
                    />
                )} */}
            </div>
            {!isPublisherEdition &&
                chargebeeInstance !== null &&
                !usage.royalties.exceedsCapacity && (
                    <div className="flex justify-center mt-6">
                        <PortalSessionButton
                            chargebeeInstance={chargebeeInstance}
                            label={
                                locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.LINKS
                                    .MANAGE_PLAN
                            }
                            onButtonViewed={onButtonViewed}
                            onSubscriptionChanged={onSubscriptionChanged}
                        />
                    </div>
                )}
            {isPublisherEdition && (
                <div className="flex justify-center mt-6">
                    <a href={mailtoContentForPublisherSupport}>
                        <Button autoWidth={true} color="secondary-light-bg">
                            <p className="px-10 text-sm font-medium tracking-wide">
                                {planLocale.CONTACT_SUPPORT}
                            </p>
                        </Button>
                    </a>
                </div>
            )}
        </CurrentPlanWrapper>
    );
};
